import { ENV } from "@/lib/env";
import { ReactNode, cache, cloneElement, isValidElement } from "react";


export const getDictionary = cache(async (locale: string) => {

  // Get client IP, fingerprint, bot info, consent and locale from middleware
  const { headers: nextHeaders } = await import('next/headers');
  const headersList = await nextHeaders();
  const clientIp = headersList.get('x-client-ip') || 'unknown';
  const fingerprint = headersList.get('x-fingerprint') || '';
  const isBot = headersList.get('x-is-bot') || 'false';
  const botType = headersList.get('x-bot-type') || 'none';
  const botCategory = headersList.get('x-bot-category') || 'none';
  const userConsent = headersList.get('x-user-consent') || 'none';
  const userLocale = headersList.get('x-user-locale') || 'unknown';

  try {
    const res = await fetch(`${ENV.BACKEND_URL}/v1/i18n/${locale}`, {
      cache: 'no-store',
      headers: {
        'X-Forwarded-For': clientIp,
        'X-Real-IP': clientIp,
        'X-Fingerprint': fingerprint,
        'X-Is-Bot': isBot,
        'X-Bot-Type': botType,
        'X-Bot-Category': botCategory,
        'X-User-Consent': userConsent,
        'X-User-Locale': userLocale,
      },
    });
    return await res.json();
  } catch (error) {
    console.error(`${new Date().toISOString()} [Next.js] ERROR Failed to fetch translations for ${locale}:`, error);
    return {};
  }
});

// Translation options interface
interface TranslationOptions {
  count?: number;
  [key: string]: any;
}


export function useServerTranslations(translation: any): { t: Function; ts: Function } {
  return {
    t: (key: string, options?: TranslationOptions) => t(translation, key, options),
    ts: (key: string, options?: TranslationOptions) => ts(translation, key, options),
  };
}

// Helper function for translation strings
export function ts(translations: any,
  key: string,
  options?: TranslationOptions): string {
  return t(translations, key, options)?.toString() || key;
}

// Helper function for client components
export function t(
  translations: any,
  key: string,
  options?: TranslationOptions
): string | ReactNode {
  let translationKey = key;

  // Handle pluralization based on count
  if (options?.count !== undefined) {
    const count = options.count;
    let suffix: string;

    if (count === 0) {
      suffix = '_zero';
    } else if (count === 1) {
      suffix = '_one';
    } else {
      suffix = '_other';
    }

    // Try to find the pluralized key first
    const pluralizedKey = `${key}${suffix}`;
    const pluralizedValue = pluralizedKey.split('.').reduce((acc, part) => acc && acc[part], translations);

    if (pluralizedValue !== undefined) {
      translationKey = pluralizedKey;
    }
  }

  if (!translationKey || typeof translationKey !== 'string') {
    console.warn('Translation key is empty or not a string', translationKey);
    return '<empty key>';
  }
  // Get the translation value
  const value = translationKey.split('.').reduce((acc, part) => acc && acc[part], translations);

  if (value === undefined) {
    return key;
  }

  const translationString = String(value);

  // If no options for interpolation, return the string as-is
  if (!options || Object.keys(options).length === 0) {
    return translationString;
  }

  // Handle interpolation
  return interpolateTranslation(translationString, options);
}

function interpolateTranslation(template: string, options: TranslationOptions): string | ReactNode {
  // Regular expression to find interpolation patterns: {{key}} or {{key:component}}
  const interpolationRegex = /\{\{([^}]+)\}\}/g;
  const parts: (string | ReactNode)[] = [];
  let lastIndex = 0;
  let match;
  let hasReactComponents = false;

  while ((match = interpolationRegex.exec(template)) !== null) {
    // Add text before the interpolation
    if (match.index > lastIndex) {
      parts.push(template.slice(lastIndex, match.index));
    }

    const interpolationKey = match[1].trim();
    let value = options[interpolationKey];

    // Handle React components
    if (isValidElement(value)) {
      hasReactComponents = true;
      parts.push(cloneElement(value as React.ReactElement, { key: `interpolation-${match.index}` }));
    } else if (value !== undefined) {
      parts.push(String(value));
    } else {
      // Keep the original interpolation if no value found
      parts.push(match[0]);
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < template.length) {
    parts.push(template.slice(lastIndex));
  }

  // If no React components, return as string
  if (!hasReactComponents) {
    return parts.join('');
  }

  // Return React fragment for components
  return parts.length === 1 ? parts[0] : parts;
}


